import { createAsyncThunk } from '@reduxjs/toolkit';

import { ID8_TABS, PROMPT_TYPE } from '../../constants';
import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import { history } from '../../store';
import { downloadDocument } from '../../utils/downloadDocument';
import downloadImage from '../../utils/downloadImage';
import generateFileName from '../../utils/generateFileName';
import { getId8Image } from '../../utils/id8ImagesRequests';
import { openProject } from '../global/globalReducer';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import {
  changeLocalMessage,
  likeLocalMessage,
  setEditableMessage,
  setIsDirty,
  shiftPromptToImages,
} from './id8Reducer';

const GET_ID8_FORM_DATA = 'id8/GET_ID8_FORM_DATA';
const RESET_SESSION = 'id8/RESET_SESSION';
const REPLY_ID8 = 'id8/REPLY_ID8';
const GENERATE_MESSAGES = 'id8/GENERATE_MESSAGES';

const GET_IDEAS_MESSAGES = 'id8/GET_IDEAS_MESSAGES';
const GET_BACKUP_MESSAGES = 'id8/GET_BACKUP_MESSAGES';
const GET_MESSAGES_BY_TAB = 'id8/GET_MESSAGES_BY_TAB';

const SAVE_MESSAGE = 'id8/SAVE_MESSAGE';
const DELETE_MESSAGE = 'id8/DELETE_MESSAGE';
const DELETE_MULTIPLE_MESSAGES = 'id8/DELETE_MULTIPLE_MESSAGES';
const SELECT_MESSAGE = 'id8/SELECT_MESSAGE';
const RESTORE_MESSAGE = 'id8/RESTORE_MESSAGE';
const LIKE_MESSAGE = 'id8/LIKE_MESSAGE';
const GET_MESSAGES_LOGS = 'id8/GET_MESSAGES_LOGS';

const CREATE_MESSAGE = 'id8/CREATE_MESSAGE';
const CLEAR_BOARD = 'id8/CLEAR_BOARD';
const EXPORT_MESSAGES = 'id8/EXPORT_MESSAGES';
const CLEAR_ALL = 'id8/CLEAR_ALL';
const RECLAIM_BACKUP = 'id8/RECLAIM_BACKUP';

const GET_IMAGES = 'id8/GET_IMAGES';
const DOWNLOAD_IMAGE = 'id8/DOWNLOAD_IMAGE';
const LIKE_IMAGE = 'id8/LIKE_IMAGE';
export const RUN_AGAIN = 'id8/RUN_AGAIN';
export const MODIFY_CONCEPT = 'id8/MODIFY_CONCEPT';
export const CUSTOM_IMAGE = 'id8/CUSTOM_IMAGE';
const REVISE_IMAGE = 'id8/REVISE_IMAGE';
const GET_PROMPT_BY_ID = 'id8/GET_PROMPT_BY_ID';
const ADD_PROMPT_TO_IMAGES = 'id8/ADD_PROMPT_TO_IMAGES';

const GET_AD_IMAGES = 'id8/GET_AD_IMAGES';
export const RECREATE_AD = 'id8/RECREATE_AD';
export const RUN_AGAIN_AD = 'id8/RUN_AGAIN_AD';
export const MODIFY_CONCEPT_AD = 'id8/MODIFY_CONCEPT_AD';

export const GET_AGENTS = 'id8/GET_AGENTS';
export const GET_PROJECT_AGENT = 'id8/GET_PROJECT_AGENT';

export const getId8FormData = createAsyncThunk(GET_ID8_FORM_DATA, async ({ id }, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => callApi.get(`/api/id8/${id}`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  dispatch(openProject(id));

  return response.data;
});

//FORM MANIPULATIONS
export const generateMessages = createAsyncThunk(
  GENERATE_MESSAGES,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, fileText } = values;

    if (!id || !url) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/id8/generate/messages?projectId=${id}`,
          {
            byPass,
            focusOn,
            url,
            fileText,
          },
          { cancelToken },
        ),
      {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const storedProjectId = getState().id8.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const resetSession = createAsyncThunk(RESET_SESSION, async (id, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => callApi.post(`/api/id8/reset/${id}`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  return response.data;
});

export const replyId8 = createAsyncThunk(REPLY_ID8, async ({ id, reply }, { rejectWithValue, dispatch, getState }) => {
  const response = await sendRequest(() => callApi.post(`/api/id8/reply?projectId=${id}`, reply), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  const storedProjectId = getState().id8.projectId;

  if (id !== storedProjectId) {
    return rejectWithValue(null);
  }

  dispatch(getMessagesByTab());

  return response.data;
});

//GET MESSAGES
const loadMessages = async (id, getState, options = {}, dispatch, rejectWithValue) => {
  const { sort, searchQuery } = getState().id8.tableSettings;

  const response = await sendRequest(
    (cancelToken) =>
      callApi.post(
        `/api/id8/message/search?sort=${sort}&id=${id}`,
        {
          ...(searchQuery ? { word: searchQuery } : {}),
          ...options,
        },
        { cancelToken },
      ),
    {
      onError: (e) => {
        if (e.response.status !== 409) {
          dispatch(somethingWrongNotification());
        }
        return rejectWithValue(e);
      },
    },
  );

  return response.data;
};
export const getIdeasMessages = createAsyncThunk(
  GET_IDEAS_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) => loadMessages(id, getState, {}, dispatch, rejectWithValue),
);
export const getBackupMessages = createAsyncThunk(
  GET_BACKUP_MESSAGES,
  async ({ id }, { getState, dispatch, rejectWithValue }) =>
    loadMessages(id, getState, { status: 'BACKUP' }, dispatch, rejectWithValue),
);

export const getMessagesByTab = createAsyncThunk(
  GET_MESSAGES_BY_TAB,
  async (id, { getState, dispatch, rejectWithValue }) => {
    const id8Tab = getState().id8.id8Tab;
    const storedProjectId = getState().id8.projectId;
    const projectId = id || storedProjectId;

    if (!projectId) {
      return rejectWithValue(null);
    }

    if (id8Tab === ID8_TABS.IDEAS) {
      dispatch(getIdeasMessages({ id: projectId }));
    }

    if (id8Tab === ID8_TABS.BACKUP) {
      dispatch(getBackupMessages({ id: projectId }));
    }
  },
);

//MESSAGE ACTIONS

export const likeMessage = createAsyncThunk(
  LIKE_MESSAGE,
  async ({ id, reaction, createType }, { dispatch, rejectWithValue }) => {
    dispatch(likeLocalMessage({ id, reaction, createType }));

    await sendRequest(() =>
      callApi.post(`/api/id8/message/reaction`, {
        id,
        ...(reaction ? { reaction } : {}),
      }),
    ),
      {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      };

    return id;
  },
);

export const saveMessageChanges = createAsyncThunk(SAVE_MESSAGE, async (_, { dispatch, getState, rejectWithValue }) => {
  const {
    editableMessage: { id, message },
  } = getState().id8;

  dispatch(changeLocalMessage({ id, message }));
  dispatch(setEditableMessage({ id: null, message: '' }));
  dispatch(setIsDirty(false));

  await sendRequest(
    () =>
      callApi.post(`/api/id8/message/edit-message/${id}`, {
        message,
      }),
    {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    },
  );
});

export const deleteMessage = createAsyncThunk(DELETE_MESSAGE, async ({ id }, { dispatch, rejectWithValue }) => {
  await sendRequest(() => callApi.delete(`/api/id8/message/${id}`), {
    onError: (e) => {
      dispatch(
        enqueueSnackbar({
          message: 'couldntDeleteMessage',
          options: {
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(e);
    },
  });

  dispatch(
    enqueueSnackbar({
      message: 'messageHasBeenDeleted',
      options: {
        variant: 'success',
      },
    }),
  );

  return id;
});

export const deleteMultipleMessages = createAsyncThunk(
  DELETE_MULTIPLE_MESSAGES,
  async (ids, { dispatch, rejectWithValue }) => {
    await sendRequest(() => callApi.delete(`/api/id8/message/delete/by/ids`, ids), {
      onError: (e) => {
        dispatch(
          enqueueSnackbar({
            message: 'couldntDeleteMessages',
            options: {
              variant: 'error',
            },
          }),
        );
        return rejectWithValue(e);
      },
    });

    dispatch(
      enqueueSnackbar({
        message: 'messagesHasBeenDeleted',
        options: {
          variant: 'success',
        },
      }),
    );

    return [];
  },
);

export const selectMessage = createAsyncThunk(
  SELECT_MESSAGE,
  async ({ id, selected, projectId }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/id8/message/${selected ? 'unselect' : 'select'}/${id}`),
      {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    if (!selected) {
      await sendRequest(() => callApi.post(`/api/id8/content/idea?projectId=${projectId}&ideaId=${id}`), {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      });
    }

    return { id, selected: !selected, createType: response.data.createType };
  },
);

export const restoreMessage = createAsyncThunk(RESTORE_MESSAGE, async ({ id }, { rejectWithValue, dispatch }) => {
  await sendRequest(() => callApi.post(`/api/id8/message/restore-message?messageId=${id}`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  return id;
});

export const getMessageLogs = createAsyncThunk(
  GET_MESSAGES_LOGS,
  async ({ messageId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(() => callApi.get(`/api/id8/message/history/${messageId}`), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });

    return response.data;
  },
);

//FILTER ACTIONS
export const createMessage = createAsyncThunk(
  CREATE_MESSAGE,
  async ({ messages, projectId }, { dispatch, rejectWithValue }) => {
    const messagesToCreate = messages.map(({ message }) => message);

    if (!messagesToCreate.length) {
      return rejectWithValue(null);
    }

    await sendRequest(
      () =>
        callApi.post(`/api/id8/message/create`, {
          messages: messagesToCreate,
          projectId,
        }),
      {
        successStatusCode: 201,
      },
      {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messageWasCreatedSuccessfully',
        options: {
          variant: 'success',
        },
      }),
    );

    dispatch(getMessagesByTab());
  },
);

export const clearBoard = createAsyncThunk(CLEAR_BOARD, async (_, { getState, dispatch }) => {
  const boardMessagesIds = getState().id8.messagesContent.map(({ id }) => id);

  dispatch(deleteMultipleMessages(boardMessagesIds));
});

export const exportMessages = createAsyncThunk(EXPORT_MESSAGES, async (_, { rejectWithValue, getState, dispatch }) => {
  const messages = getState().id8.messagesContent.map(({ id }) => id);
  const projectName = getState().id8.projectName;

  if (!messages.length) {
    dispatch(
      enqueueSnackbar({
        message: 'noMessagesAvailable',
        options: {
          variant: 'warning',
          subMessage: 'pleaseGenerateMessagesBefore',
        },
      }),
    );
    return rejectWithValue(null);
  }

  const response = await sendRequest(
    () =>
      callApi.request({
        url: `/api/id8/message/export`,
        method: 'POST',
        responseType: 'blob',
        data: messages,
      }),
    {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    },
  );

  const fileName = generateFileName({ name: projectName, ext: 'txt' });

  downloadDocument(fileName, response.data);
});

export const clearAll = createAsyncThunk(CLEAR_ALL, async (projectId, { rejectWithValue, dispatch, getState }) => {
  const response = await sendRequest(() => callApi.post(`/api/id8/clear?id=${projectId}`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  const storedProjectId = getState().id8.projectId;

  if (projectId !== storedProjectId) {
    return rejectWithValue(null);
  }

  return response.data;
});

export const reclaimBackup = createAsyncThunk(
  RECLAIM_BACKUP,
  async (projectId, { rejectWithValue, dispatch, getState }) => {
    const response = await sendRequest(() => callApi.post(`/api/id8/message/reclaim?projectId=${projectId}`), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });

    const storedProjectId = getState().id8.projectId;

    if (projectId !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(getMessagesByTab(projectId));

    return response.data;
  },
);

// IMAGES ACTIONS
const loadImages = async ({ projectId, getState, options = {}, dispatch, rejectWithValue }) => {
  const { searchQuery, page, sort, size, ...additionalOptions } = options;

  const response = await sendRequest(
    () =>
      callApi.post(`/api/id8/content/search?projectId=${projectId}&page=${page}&size=${size}&sort=${sort}`, {
        ...(searchQuery ? { query: searchQuery } : {}),
        ...additionalOptions,
      }),
    {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    },
  );

  return response.data;
};

export const getImages = createAsyncThunk(GET_IMAGES, async (projectId, { rejectWithValue, dispatch, getState }) => {
  const { searchQuery, page, sort, size } = getState().id8.imagesTableSettings;
  const response = await loadImages({
    projectId,
    getState,
    options: { searchQuery, page, sort, size, types: [PROMPT_TYPE.IDEA] },
    dispatch,
    rejectWithValue,
  });

  return { content: response.content, totalElements: response.totalElements };
});

export const getAdImages = createAsyncThunk(
  GET_AD_IMAGES,
  async (projectId, { rejectWithValue, dispatch, getState }) => {
    const { searchQuery, page, sort, size } = getState().id8.adImagesTableSettings;

    const response = await loadImages({
      projectId,
      getState,
      options: { searchQuery, page, sort, size, types: [PROMPT_TYPE.RECREATE] },
      dispatch,
      rejectWithValue,
    });

    return { content: response.content, totalElements: response.totalElements };
  },
);

export const downloadId8Image = createAsyncThunk(DOWNLOAD_IMAGE, async (imageId, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => getId8Image(imageId), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  downloadImage(imageId, response.data);
});

export const likeId8Image = createAsyncThunk(
  LIKE_IMAGE,
  async ({ imageId, newReaction, updatedOn, promptType }, { rejectWithValue, dispatch }) => {
    sendRequest(() => callApi.post(`/api/id8/content/react?imageId=${imageId}&reaction=${newReaction}`), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });

    return { imageId, newReaction, updatedOn, promptType };
  },
);

export const runAgain = createAsyncThunk(RUN_AGAIN, async (projectId, { rejectWithValue, dispatch, getState }) => {
  const instructionsId = getState().id8.imagesContent[0]?.id;

  await sendRequest(() => callApi.post(`/api/id8/content/re-generate?bucketId=${instructionsId}`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  const storedProjectId = getState().id8.projectId;

  if (projectId !== storedProjectId) {
    return rejectWithValue(null);
  }
});

export const modifyConcept = createAsyncThunk(
  MODIFY_CONCEPT,
  async ({ newConcept }, { rejectWithValue, dispatch, getState }) => {
    const bucketId = getState().id8.imagesContent[0]?.id;

    if (!bucketId) {
      return rejectWithValue(null);
    }

    await sendRequest(() => callApi.post(`/api/id8/content/modify/concept?bucketId=${bucketId}`, newConcept), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });
  },
);

export const customImage = createAsyncThunk(
  CUSTOM_IMAGE,
  async ({ description, projectId }, { rejectWithValue, dispatch }) => {
    await sendRequest(() => callApi.post(`/api/id8/content/custom-idea?projectId=${projectId}`, description), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });
  },
);

export const reviseImage = createAsyncThunk(
  REVISE_IMAGE,
  async ({ imageId, description, file }, { rejectWithValue, dispatch }) => {
    const formData = new FormData();

    if (file) {
      formData.append('file', file, file.name);
    }

    await sendRequest(
      () =>
        callApi.request({
          url: `/api/id8/content/revise?imageId=${imageId}&revision=${description}`,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          method: 'POST',
          data: formData,
        }),
      {
        onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

export const getPromptById = createAsyncThunk(
  GET_PROMPT_BY_ID,
  async ({ bucketId, promptType }, { rejectWithValue, dispatch, getState }) => {
    const response = await sendRequest(() => callApi.get(`/api/id8/content/find/bucket/by/id?bucketId=${bucketId}`), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });

    const isPromptExist =
      getState().id8.imagesContent.find((image) => image.id === response.data.id) ||
      getState().id8.adImagesContent.find((image) => image.id === response.data.id);

    if (!isPromptExist) {
      await dispatch(addPromptToImages({ promptId: bucketId, promptType }));
    }

    return { prompt: response.data, promptType };
  },
);

export const addPromptToImages = createAsyncThunk(
  ADD_PROMPT_TO_IMAGES,
  async ({ promptId, promptType }, { dispatch, getState }) => {
    const page = getState().id8.imagesTableSettings.page;

    if (page !== 1) {
      dispatch(
        enqueueSnackbar({
          message: 'cardWithIdHasBeenAdded',
          options: { variant: 'success', messageParams: { promptId } },
        }),
      );

      return;
    }

    const EMPTY_PROMPT = {
      id: promptId,
      generating: true,
      images: [],
    };

    dispatch(shiftPromptToImages({ prompt: EMPTY_PROMPT, promptType }));
  },
);

export const getAgents = createAsyncThunk(GET_AGENTS, async (_, { rejectWithValue, dispatch }) => {
  const response = await sendRequest(() => callApi.post(`/api/id8/agent/search?page=1&size=999`), {
    onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
  });

  return response.data.content;
});

export const getProjectAgent = createAsyncThunk(GET_PROJECT_AGENT, async (agentId) => {
  const response = await sendRequest(() => callApi.get(`/api/id8/agent/${agentId}`));

  return response.data;
});

// AD IMAGES
export const recreateAd = createAsyncThunk(RECREATE_AD, async ({ projectId, image, description }) => {
  const formData = new FormData();
  formData.append('file', image, image.name);

  const response = await sendRequest(() =>
    callApi.request({
      url: `/api/id8/content/recreate?projectId=${projectId}&description=${description}`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data: formData,
    }),
  );

  return response.data;
});

export const runAgainAd = createAsyncThunk(
  RUN_AGAIN_AD,
  async ({ projectId }, { rejectWithValue, dispatch, getState }) => {
    const bucketId = getState().id8.adImagesContent[0]?.id;

    if (!bucketId) {
      return rejectWithValue(null);
    }

    await sendRequest(() => callApi.post(`/api/id8/content/re-generate?bucketId=${bucketId}`), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });

    const storedProjectId = getState().id8.projectId;

    if (projectId !== storedProjectId) {
      return rejectWithValue(null);
    }
  },
);

export const modifyAdConcept = createAsyncThunk(
  MODIFY_CONCEPT_AD,
  async ({ newConcept }, { rejectWithValue, dispatch, getState }) => {
    const bucketId = getState().id8.adImagesContent[0]?.id;

    if (!bucketId) {
      return rejectWithValue(null);
    }

    await sendRequest(() => callApi.post(`/api/id8/content/modify/concept?bucketId=${bucketId}`, newConcept), {
      onError: (e) => id8ErrorHandler(e, dispatch, rejectWithValue),
    });
  },
);

const id8ErrorHandler = (e, dispatch, rejectWithValue) => {
  if (e.response.status === 409) {
    dispatch(
      enqueueSnackbar({
        message: 'projectInUse',
        options: {
          variant: 'warning',
          subMessage: 'thisProjectIsCurrentlyUsing',
          subMessageParams: {
            username: e.response.data.username ?? '',
          },
        },
      }),
    );

    history.push('/projects');
    return rejectWithValue(e);
  }

  dispatch(somethingWrongNotification());
  return rejectWithValue(e);
};
